import { useEffect, useState } from 'react';
import ErrorDisplay from '../../../../components/ErrorDisplay';
import { ToolPicker } from '../../../../pickers/tool/ToolPicker';
import type { Tool } from '../../../../types/tools';
import type { JourneyItemTemplateToolFirestore } from '../../services/journeyTypes';
import { JourneyItemFormActions } from './JourneyItemFormActions';
import { JourneyItemFormFields } from './JourneyItemFormFields';
import { useJourneyItemForm } from './useJourneyItemForm';

interface ToolSpecificFormData {
    imageUrl: string;
    toolName: string;
    toolId: string;
}

interface ToolJourneyItemFormProps {
    itemTemplate?: JourneyItemTemplateToolFirestore;
    initialOrder: number;
    onSave: (item: JourneyItemTemplateToolFirestore) => void;
    onSaveAndContinue?: (item: JourneyItemTemplateToolFirestore) => void;
    onCancel: () => void;
}

export const ToolJourneyItemForm = ({ itemTemplate, initialOrder, onSave, onSaveAndContinue, onCancel }: ToolJourneyItemFormProps) => {
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue,
    } = useJourneyItemForm({
        itemTemplate,
        initialOrder,
        defaultProgressType: 'fixed',
    });

    const [specificFormData, setSpecificFormData] = useState<ToolSpecificFormData>({
        imageUrl: '',
        toolName: '',
        toolId: '',
    });

    useEffect(() => {
        if (itemTemplate && itemTemplate.id) {
            setSpecificFormData({
                imageUrl: itemTemplate.imageUrl,
                toolName: itemTemplate.toolName,
                toolId: itemTemplate.toolId,
            });
        }
    }, [itemTemplate]);

    const handleChange = (field: string, value: string | number | boolean) => {
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value);
        } else {
            setSpecificFormData((prev) => ({ ...prev, [field]: value }));
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.imageUrl.trim()) {
            return 'Image URL is required for tool items';
        }
        if (!specificFormData.toolName.trim()) {
            return 'Tool Name is required for tool items';
        }
        if (!specificFormData.toolId.trim()) {
            return 'Tool ID is required for tool items';
        }
        return null;
    };

    const createItem = (baseData: Partial<JourneyItemTemplateToolFirestore>): JourneyItemTemplateToolFirestore => {
        return {
            ...baseData,
            type: 'tool',
            maxProgress: 1,
            imageUrl: specificFormData.imageUrl,
            toolName: specificFormData.toolName,
            toolId: specificFormData.toolId,
        } as JourneyItemTemplateToolFirestore;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createItem, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createItem, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormFields
                formData={commonFormData}
                itemType="tool"
                disableProgressType={true}
                onChange={handleChange}
            />

            <h2 className="text-lg font-bold mt-2">Tool-specific fields</h2>
            <hr className="-mt-4" />

            <ToolPicker
                value={specificFormData.toolId}
                onChange={(tool: Tool | null) => {
                    if (tool) {
                        setSpecificFormData((prev) => ({
                            ...prev,
                            toolId: tool.id,
                            toolName: tool.bannerText,
                            imageUrl: tool.bannerPath,
                        }));
                    } else {
                        setSpecificFormData((prev) => ({
                            ...prev,
                            toolId: '',
                            toolName: '',
                            imageUrl: '',
                        }));
                    }
                }}
                label="Tool"
                required
            />

            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!itemTemplate?.id}
            />
        </form>
    );
};
